import ArticleCard from '@/components/orders/ArticleCard'
import ArticleFormModal from '@/components/orders/ArticleFormModal'
import ConfirmOrderModal from '@/components/orders/ConfirmOrderModal'
import { useRole } from '@/hooks/useRole'
import { ArticleService, Order, OrderConfirmation, Printer, SharedProps } from '@/types'
import { formatCurrency, getCustomerInfo } from '@/utils/format'
import { canDeleteOrder } from '@/utils/orders'
import { printOrderTickets } from '@/utils/print'
import { router, usePage } from '@inertiajs/react'
import {
  ActionIcon,
  Box,
  Button,
  Checkbox,
  Divider,
  Group,
  Paper,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { IconChevronRight, IconPlus, IconTrash } from '@tabler/icons-react'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'

export default function OrderCard({
  order,
  organizationSlug,
  articleServices,
  printers = [],
  autoOpen,
  action = 'create',
  showPlannedDate = true,
  showCustomerTitle = true,
  showDeleteOrder = true,
  embedded = false,
  heading,
}: {
  order: Order
  organizationSlug: string
  articleServices: ArticleService[]
  printers?: Printer[]
  autoOpen: boolean
  action?: 'create' | 'edit'
  showPlannedDate?: boolean
  showCustomerTitle?: boolean
  showDeleteOrder?: boolean
  embedded?: boolean
  heading?: string
}) {
  const { current_organization } = usePage<SharedProps>().props
  const { isBoutique, isAdmin, isAccueil } = useRole()
  const isLocked = isBoutique && !!order.dispatched_at
  const canDelete = canDeleteOrder(order, { isAdmin, isAccueil })
  const showCreateActions = action === 'create' && !isLocked
  const isCompanyCustomer = Boolean(order.contract && order.contract.customer_organization_id === null)
  const [isDispatching, setIsDispatching] = useState(false)
  const [opened, { open, close }] = useDisclosure(false)
  const [shouldPrint, setShouldPrint] = useState(true)
  const [confirmOpened, confirmModal] = useDisclosure(false)
  const hasServices = order.articles.some((article) => article.services.length > 0)
  const asksPayment = showCreateActions && !order.contract_id

  useEffect(() => {
    if (autoOpen) open()
  }, [autoOpen])

  const handleAddArticle = (data: {
    articleName: string
    serviceName: string
    price: number
    instructions: string
    reference: string
  }) => {
    router.post(
      `/organizations/${organizationSlug}/orders/${order.id}/articles`,
      {
        article: { article_name: data.articleName, reference: data.reference },
      },
      {
        onSuccess: (page) => {
          const orders = page.props.orders as Order[] | undefined
          const updatedOrder = orders ? orders.find((o) => o.id === order.id) : (page.props.order as Order | undefined)
          const newItem = updatedOrder
            ? [...updatedOrder.articles]
                .reverse()
                .find((item) => item.article_name === data.articleName && item.services.length === 0)
            : null

          if (newItem?.id) {
            router.post(
              `/organizations/${organizationSlug}/orders/${order.id}/articles/${newItem.id}/services`,
              {
                service: {
                  service_name: data.serviceName,
                  instructions: data.instructions,
                  price: data.price,
                },
              },
              { preserveScroll: true, onSuccess: close }
            )
          } else {
            close()
          }
        },
      }
    )
  }

  const handleDeleteOrder = () => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette commande ?')) {
      return
    }
    router.delete(`/organizations/${organizationSlug}/orders/${order.id}`, {
      preserveScroll: true,
    })
  }

  const handleDispatch = () => {
    setIsDispatching(true)
    router.post(
      `/organizations/${organizationSlug}/orders/${order.id}/dispatch`,
      {},
      {
        onFinish: () => setIsDispatching(false),
        preserveScroll: true,
        onSuccess: () => {
          if (shouldPrint) {
            router.visit(`/organizations/${organizationSlug}/orders/${order.id}/print`)
          }
        },
      }
    )
  }

  const handleConfirm = (confirmation: OrderConfirmation) => {
    setIsDispatching(true)
    router.post(
      `/organizations/${organizationSlug}/orders/${order.id}/dispatch`,
      { payment_method: confirmation.paymentMethod },
      {
        onFinish: () => setIsDispatching(false),
        preserveScroll: true,
        onSuccess: (page) => {
          const stillDraft = (page.props.orders as Order[] | undefined)?.some((draft) => draft.id === order.id)
          if (stillDraft || !confirmation.shouldPrint) return

          void printOrderTickets({
            organizationSlug,
            orderId: order.id,
            cloudprntEnabled: Boolean(current_organization?.cloudprnt_enabled),
            printerId: confirmation.printerId,
          })
        },
      }
    )
  }

  const handleUpdatePlannedDate = (date: string) => {
    router.patch(
      `/organizations/${organizationSlug}/orders/${order.id}`,
      { order: { planned_date: date } },
      { preserveScroll: true }
    )
  }

  return (
    <Paper radius="lg" withBorder={!embedded} p={embedded ? 0 : 'lg'}>
      <Stack gap="md">
        <Stack gap="md">
          <Group justify="space-between" align="flex-start">
            <Box>
              {heading ? (
                <Text fw={700}>{heading}</Text>
              ) : showCustomerTitle ? (
                <Group gap="xs">
                  <Title order={3}>{getCustomerInfo(order).name}</Title>
                </Group>
              ) : null}
            </Box>

            <Group gap="xs">
              {!isLocked && (
                <Button variant="light" leftSection={<IconPlus size={14} />} onClick={open}>
                  Ajouter un article
                </Button>
              )}
              {showDeleteOrder && canDelete && (
                <ActionIcon
                  variant="subtle"
                  color="red"
                  title="Supprimer la commande"
                  onClick={handleDeleteOrder}
                  size="lg"
                >
                  <IconTrash size={18} />
                </ActionIcon>
              )}
            </Group>
          </Group>

          {order.articles.map((item) => (
            <ArticleCard
              key={item.id}
              item={item}
              articleServices={articleServices}
              organizationSlug={organizationSlug}
              orderId={order.id}
              isCompanyCustomer={isCompanyCustomer}
              existingReferences={order.articles.filter((a) => a.id !== item.id).map((a) => a.reference)}
              isLocked={isLocked}
              canDelete={canDelete}
            />
          ))}

          {asksPayment && (
            <ConfirmOrderModal
              order={order}
              printers={printers}
              opened={confirmOpened}
              confirming={isDispatching}
              onClose={confirmModal.close}
              onConfirm={handleConfirm}
            />
          )}

          <ArticleFormModal
            opened={opened}
            onClose={close}
            onSubmit={handleAddArticle}
            articleServices={articleServices}
            isCompanyCustomer={isCompanyCustomer}
            existingReferences={order.articles.map((a) => a.reference)}
            title="Ajouter un article"
            submitLabel="Ajouter"
          />

          {(showPlannedDate || showCreateActions) && (
            <>
              <Divider />

              <Group justify="space-between" align="center">
                {showPlannedDate ? (
                  <Box
                    style={{
                      visibility: hasServices ? 'visible' : 'hidden',
                    }}
                  >
                    <TextInput
                      type="date"
                      label="Date de collecte"
                      value={order.planned_date ? dayjs(order.planned_date).format('YYYY-MM-DD') : ''}
                      onChange={(e) => handleUpdatePlannedDate(e.currentTarget.value)}
                      size="xs"
                      w={150}
                      readOnly={isBoutique || isLocked}
                    />
                  </Box>
                ) : (
                  <Box />
                )}

                <Group gap="md" align="center">
                  {asksPayment && (
                    <Button
                      disabled={!hasServices}
                      rightSection={<IconChevronRight size={18} />}
                      onClick={confirmModal.open}
                    >
                      Passer la commande ({formatCurrency(order.price)})
                    </Button>
                  )}
                  {showCreateActions && !asksPayment && (
                    <>
                      <Checkbox
                        label="Imprimer le bon"
                        checked={shouldPrint}
                        onChange={(event) => setShouldPrint(event.currentTarget.checked)}
                      />
                      <Button
                        disabled={!hasServices}
                        loading={isDispatching}
                        rightSection={<IconChevronRight size={18} />}
                        onClick={handleDispatch}
                      >
                        {isBoutique ? 'Confirmer et envoyer' : 'Passer la commande'} ({formatCurrency(order.price)})
                      </Button>
                    </>
                  )}
                </Group>
              </Group>
            </>
          )}
        </Stack>
      </Stack>
    </Paper>
  )
}
